/**
 * User Categories Tab
 * CRUD management for UserCategory reference data.
 */

import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Tooltip,
    Stack,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { api } from '@shared/api/client';

interface UserCategory {
    id: string;
    name: string;
    description?: string | null;
    is_active: boolean;
    created_at?: string;
}

interface CategoryFormData {
    name: string;
    description: string;
    is_active: boolean;
}

const emptyForm: CategoryFormData = { name: '', description: '', is_active: true };

export function UserCategoriesTab() {
    const [categories, setCategories] = useState<UserCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<UserCategory | null>(null);
    const [formData, setFormData] = useState<CategoryFormData>(emptyForm);
    const [formError, setFormError] = useState<string | null>(null);

    const loadCategories = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await (api as any).getUserCategories();
            setCategories(res.data?.data ?? []);
        } catch {
            setError('Failed to load user categories.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadCategories(); }, []);

    const handleOpenCreate = () => {
        setEditTarget(null);
        setFormData(emptyForm);
        setFormError(null);
        setDialogOpen(true);
    };

    const handleOpenEdit = (cat: UserCategory) => {
        setEditTarget(cat);
        setFormData({ name: cat.name, description: cat.description ?? '', is_active: cat.is_active });
        setFormError(null);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            setFormError('Name is required.');
            return;
        }
        try {
            setSaving(true);
            setFormError(null);
            if (editTarget) {
                await (api as any).updateUserCategory(editTarget.id, formData);
            } else {
                await (api as any).createUserCategory(formData);
            }
            setDialogOpen(false);
            await loadCategories();
        } catch {
            setFormError('Failed to save category. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            {/* Toolbar */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#04377b' }}>
                    User Categories ({categories.length})
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={loadCategories} disabled={loading}
                            sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: '50%', width: 30, height: 30 }}>
                            <RefreshIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        sx={{ bgcolor: '#04377b', '&:hover': { bgcolor: '#032e64' }, textTransform: 'none' }}
                    >
                        New Category
                    </Button>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#04377b' }} />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={0} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(4, 55, 123, 0.04)' }}>
                                {['Name', 'Description', 'Status', 'Created', 'Actions'].map((h) => (
                                    <TableCell key={h} sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.75rem', py: 1 }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No categories yet. Click "New Category" to create one.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                categories.map((cat) => (
                                    <TableRow
                                        key={cat.id}
                                        sx={{
                                            '&:nth-of-type(odd)': { bgcolor: 'rgba(4, 55, 123, 0.015)' },
                                            '&:hover': { bgcolor: 'rgba(4, 55, 123, 0.03)' },
                                        }}
                                    >
                                        <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.8125rem' }}>{cat.name}</TableCell>
                                        <TableCell sx={{ py: 1, fontSize: '0.75rem', color: 'text.secondary', maxWidth: 300 }}>
                                            {cat.description || '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                            <Chip
                                                label={cat.is_active ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={cat.is_active ? 'success' : 'default'}
                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                            {cat.created_at
                                                ? new Date(cat.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                                : '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => handleOpenEdit(cat)} sx={{ color: '#04377b' }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: '#04377b' }}>
                    {editTarget ? 'Edit Category' : 'New User Category'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <TextField
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                            fullWidth
                            size="small"
                            required
                            error={!!formError && !formData.name.trim()}
                        />
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData((f) => ({ ...f, is_active: e.target.checked }))}
                                    size="small"
                                />
                            }
                            label={<Typography variant="body2">Active</Typography>}
                        />
                        {formError && <Alert severity="error" sx={{ py: 0 }}>{formError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
                        sx={{ bgcolor: '#04377b', '&:hover': { bgcolor: '#032e64' } }}
                    >
                        {saving ? 'Saving...' : (editTarget ? 'Save Changes' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
